import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';

const FAQS = [
  {
    q: 'What is tax-loss harvesting?',
    a: 'Tax-loss harvesting (TLH) is the practice of selling investments at a loss to OFFSET capital gains you\'ve realized elsewhere — reducing your current-year tax bill. If you sold AAPL at a $5,000 gain and you also have an unrealized $3,000 loss in MMM, selling MMM converts the paper loss into a deductible loss that knocks your taxable gain down to $2,000.',
  },
  {
    q: 'What\'s the wash-sale rule?',
    a: 'The IRS will DISALLOW your loss deduction if you buy back the same stock (or a "substantially identical" one) within 30 days BEFORE or AFTER the sale. That\'s a 61-day window total. You can wait it out (31 days minimum), buy a different ticker in the same sector, or buy a sister ETF that\'s similar but not identical. DiviDrip\'s Tax Lots page flags exactly which of your lots are wash-sale-safe.',
  },
  {
    q: 'How much can a tax loss save me?',
    a: 'Losses offset gains dollar-for-dollar by holding period (short-term losses offset short-term gains first, long-term against long-term). Excess losses up to $3,000/year can offset ordinary income. Beyond that, losses CARRY FORWARD indefinitely. Practical example: a high-income filer who harvests $20,000 of long-term losses against $20,000 of long-term gains saves 15-23.8% federal tax — roughly $3,000-$4,800 in actual money.',
  },
  {
    q: 'Does DiviDrip automate any of this?',
    a: 'DiviDrip surfaces the candidates and the wash-sale risks. The "Tax Lots → Harvest Candidates" filter shows you exactly which lots are at a loss AND have no buys in the last 30 days. The December TaxHarvestBanner pops up on the Portfolio page once a year reminding you what\'s harvestable. The actual sell trade still happens at your broker — DiviDrip doesn\'t execute trades. Always confirm specifics with a tax pro.',
  },
];

export default function LearnTaxLossHarvesting() {
  const jsonLd = {
    '@context': 'https://schema.org', '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } })),
  };
  return (
    <LearnPage
      title="The Tax-Loss Harvesting Playbook for Dividend Investors (2026) | DiviDrip"
      description="The single biggest December tax move most investors skip. Wash-sale rules, the 31-day window, the swap-to-a-substitute trick, and how DiviDrip's Tax Lots → Harvest Candidates filter finds wash-sale-safe lots for you."
      canonical="https://twylightcrow.com/learn/tax-loss-harvesting-playbook"
      guidePath="/learn/tax-loss-harvesting-playbook"
      sectionLabel="Tax Strategy"
      sectionAnchor="tax"
      kicker="Learn · Year-end tactic"
      h1="The Tax-Loss Harvesting Playbook for Dividend Investors"
      datePublished="2026-05-24"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/why-drip-roth', label: 'Why DRIP belongs in a Roth IRA' },
        { to: '/learn/qualified-vs-ordinary-dividends', label: 'Qualified vs ordinary dividends' },
        { to: '/learn/dividend-cuts-of-the-decade', label: 'Dividend cuts of the decade' },
        { to: '/learn/bond-yields-vs-dividend-yields', label: 'Bond yields vs dividend yields' },
        { to: '/glossary', label: 'Glossary — every dividend term defined' },
      ]}
    >
      <p>
        Every December, investors leave thousands of dollars on the table by
        skipping the single most powerful year-end tax move available to them:{' '}
        <strong>tax-loss harvesting</strong>. The math is simple, the timing is
        narrow (you have until Dec 31), and DiviDrip surfaces the candidates for
        you. Here&apos;s the full playbook.
      </p>
      <p>
        <em>Not tax advice. Always confirm specifics with a CPA — your bracket,
        state rules, and broker-specific lot accounting all factor in.</em>
      </p>

      <h2>What is tax-loss harvesting?</h2>
      <p>
        It&apos;s the practice of <strong>selling losers to offset winners</strong>.
        When you sell a stock for less than you paid, the loss can be deducted
        against capital gains you&apos;ve already realized this year. Net less
        gain → less tax owed.
      </p>
      <p>Worked example:</p>
      <ul>
        <li>You bought 100 shares of AAPL at $150. Sold at $200. <strong>+$5,000 long-term gain.</strong></li>
        <li>You bought 100 shares of MMM at $130. Currently worth $100. <strong>-$3,000 unrealized loss.</strong></li>
        <li>If you do nothing: you owe 15-23.8% federal on the $5,000 = ~$750-$1,190.</li>
        <li>If you sell MMM and realize the $3,000 loss: it offsets the AAPL gain. Net taxable gain = $2,000. Tax owed ~$300-$476.</li>
        <li><strong>You just saved ~$450-$700 in tax</strong> for a few minutes of work.</li>
      </ul>

      <h2>The wash-sale rule (the one trap)</h2>
      <p>
        The IRS doesn&apos;t want you faking losses by selling MMM Friday and
        rebuying it Monday. The <strong>wash-sale rule</strong> disallows the
        loss deduction if you buy back the same stock (or a &quot;substantially
        identical&quot; one) within <strong>30 days BEFORE or AFTER</strong> the
        sale. That&apos;s a 61-day window total.
      </p>
      <p>Three ways to play around the rule:</p>
      <ol>
        <li>
          <strong>Wait 31 days.</strong> Sell MMM, wait. Day 32, buy MMM back if
          you still want the position. Risk: you miss whatever the stock does in
          that month.
        </li>
        <li>
          <strong>Buy a different stock in the same sector.</strong> Sell MMM,
          buy HON or ITW. Different ticker, similar exposure. Not &quot;substantially
          identical&quot; per IRS interpretation.
        </li>
        <li>
          <strong>Buy a sister ETF.</strong> Sell SCHD, buy VYM for 31 days,
          then swap back. Both are quality-dividend ETFs but they track
          different indexes, so most tax pros treat them as not substantially
          identical. (Confirm with your CPA — this gets nuanced.)
        </li>
      </ol>
      <p>
        Wash sales also apply across YOUR accounts (taxable + IRA + spouse&apos;s
        accounts). Selling in your brokerage while your spouse is buying the
        same ticker triggers the disallowance.
      </p>

      <h2>The DiviDrip workflow</h2>
      <p>The site does the hardest part for you. Here&apos;s the 3-step flow:</p>
      <ol>
        <li>
          <strong>December reminder.</strong> The <strong>TaxHarvestBanner</strong>{' '}
          appears on your Portfolio page from Dec 1 to Dec 31. It shows your
          actual harvestable losses across long-term and short-term buckets.
          If there&apos;s nothing to harvest, the banner doesn&apos;t show.
        </li>
        <li>
          <strong>Open the Tax Lots page.</strong> Switch the filter to{' '}
          <strong>&quot;Harvest Candidates&quot;</strong>. This shows ONLY lots
          that are (a) at a loss and (b) have no buys in the same ticker over the
          last 30 days — meaning no wash-sale risk. Sorted biggest loss first.
        </li>
        <li>
          <strong>Pick lots, sell at your broker, mark them in DiviDrip.</strong>{' '}
          Execute the sells at your brokerage (Fidelity, Schwab, etc.). When you
          log the sell back into DiviDrip, tag it with the <em>tax_harvest=true</em>{' '}
          flag — that pulls it into the year-end Tax-Harvest Report you can hand
          to your CPA in January.
        </li>
      </ol>

      <h2>Harvest BEFORE you rebuy, not after</h2>
      <p>
        The most common mistake: investors see MMM down and want to dollar-cost
        average into it. They buy 20 more shares Dec 15. Then on Dec 28 they
        realize they could have harvested the loss on their original lots — but
        by then the 30-day clock is running. The newer buy WASHED the older
        loss.
      </p>
      <p>
        The fix: <strong>harvest FIRST, rebuy LATER</strong>. If you want to add
        to a name and harvest a loss in it, sell the loss lots first, wait 31
        days, then add. Or buy a different ticker in the meantime to keep market
        exposure.
      </p>

      <h2>The substitute-holding swap (advanced)</h2>
      <p>
        For investors who want to stay invested in the same sector but realize
        the loss, here are common &quot;not substantially identical&quot; pairs.
        Confirm any swap with your CPA — the IRS hasn&apos;t published a
        definitive list, so professional judgement matters.
      </p>
      <table className="learn-table">
        <thead>
          <tr><th>Sell</th><th>Buy (31 days)</th><th>Strategy match</th></tr>
        </thead>
        <tbody>
          <tr><td>SCHD</td><td>VYM, DGRO</td><td>Quality dividend / dividend appreciation</td></tr>
          <tr><td>VTI</td><td>ITOT, SCHB</td><td>Total US market</td></tr>
          <tr><td>VOO / SPY</td><td>IVV, SPLG</td><td>S&amp;P 500 (different fund family)</td></tr>
          <tr><td>VXUS</td><td>IXUS</td><td>International ex-US</td></tr>
          <tr><td>JEPI</td><td>JEPQ, DIVO</td><td>Covered-call income (different underlying)</td></tr>
        </tbody>
      </table>

      <h2>The $3,000 ordinary-income offset</h2>
      <p>
        After offsetting all capital gains, any REMAINING net loss can offset up
        to <strong>$3,000 of ordinary income</strong> per year. Anything beyond
        that carries FORWARD indefinitely — there&apos;s no expiration on a tax
        loss carryforward. Big harvest years can shelter ordinary income for
        decades to come.
      </p>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }) => (
          <div key={q} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>

      <h2>Bottom line</h2>
      <p>
        Open <Link to="/dashboard">DiviDrip</Link> in December. If the
        TaxHarvestBanner pops up, click through to <strong>Tax Lots → Harvest
        Candidates</strong>. The site shows you the exact lots that are
        wash-sale-safe to sell. Execute, then mark them as harvested. Hand the
        printout to your CPA in January.
      </p>
      <p>
        Most retail investors leave this money on the table. The ones who don&apos;t
        save hundreds to thousands of dollars per year, year after year. The
        compounding tax-savings tail is real.
      </p>
    </LearnPage>
  );
}
